import Avatar from '@components/common/avatar'
import { useSessionContext } from '@contexts/session'
import { QUIZ_USERNAME_LOCAL_STORAGE_KEY } from '@lib/constants/quizzes'
import { QuizProblemType } from '@lib/types/quizzes'
import { getFromLocalStorage } from '@lib/utils/local-storage'
import {
  generateRandomRank,
  onCreateAccountToSave,
  onQuizPlay,
} from '@lib/utils/quizzes'
import { QuizStat } from '@pages/quizzes/[quizName]'
import { Line } from 'rc-progress'
import { useCallback, useMemo, useState } from 'react'
import Link from 'next/link'
import {
  FacebookIcon,
  FacebookShareButton,
  TelegramIcon,
  TelegramShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share'
import { useRouter } from 'next/router'
import QuizSummaryProblemCard from '@components/quizzes/arena/result/quiz-summary-problem-card'
import Spinner from '@components/common/spinner'
import { handleRedirectionWithSignupSource } from '@common/utils'
import classNames from 'classnames'
import { ROUTES } from '@common/enums'

type Props = {
  points: number
  problems: QuizProblemType[]
  quizTitle: string
  quizId: number
  slug: string
  playCount: number
  isLessonQuiz: boolean
}

const Result = ({
  points,
  playCount,
  problems,
  quizTitle,
  quizId,
  slug,
  isLessonQuiz,
}: Props) => {
  const {
    loggedIn,
    name,
    student,
    isPaidUser,
    id: userId,
  } = useSessionContext()
  const [isLoading, setIsLoading] = useState(false)
  const totalProblemsAnsweredCorrectly = useMemo(() => {
    let totalProblemsAnsweredCorrectly = 0
    problems.forEach(
      (problem) => problem.isCorrect && totalProblemsAnsweredCorrectly++
    )
    return totalProblemsAnsweredCorrectly
  }, [])

  const { totalParticipants, userRank } = useMemo(
    () => generateRandomRank({ quizId, userId, playCount }),
    []
  )

  const getUsername = useCallback(
    () =>
      loggedIn
        ? name
        : getFromLocalStorage(QUIZ_USERNAME_LOCAL_STORAGE_KEY) ?? 'Guest',
    []
  )

  const router = useRouter()
  const quizUrl = `https://codingal.com/quizzes/${
    isLessonQuiz ? '' : router.query?.quizName
  }`

  // TODO: Add real rank here once leaderboard is ready
  const shareText = useMemo(
    () =>
      `I ranked ${userRank} out of ${totalParticipants} students on Codingal today in ${quizTitle} quiz.` +
      isLessonQuiz
        ? ` Check out exciting quizzes at ${quizUrl}`
        : ` Check it out here > ${quizUrl} via @codingal`,
    []
  )

  const handlePlayAgain = async () => {
    if (isLoading) return
    setIsLoading(true)
    await onQuizPlay(quizId, slug)
    // `onQuizPlay` will direct to the arena page. Hence when on the arena page needs to be reloaded to update
    // the data
    if (window.location.pathname === `/quizzes/${slug}/arena/`) {
      router.reload()
    }
  }

  const accuracyPercentage =
    (totalProblemsAnsweredCorrectly * 100) / problems.length
  return (
    <div className="h-screen overflow-auto">
      <div className="flex flex-col px-5 py-8 m-5 space-y-5 bg-white rounded-lg lg:px-8 lg:max-w-md lg:mx-auto">
        <p className="text-lg text-center lg:text-2xl font-600">Quiz Summary</p>
        <p className="leading-tight text-center lg:text-xl font-600">
          {quizTitle}
        </p>
        <div className="flex items-center p-5 space-x-4 rounded-lg bg-grey-300 lg:px-20">
          <Avatar size="medium" />
          <div className="flex flex-col">
            <p className="font-600 lg:text-lg">{getUsername()}</p>
            {loggedIn && student && (
              <p className="text-sm text-grey">{student.grade}</p>
            )}
          </div>
        </div>
        <div className="flex justify-between md:justify-start md:space-x-20">
          <QuizStat
            image="rank"
            stat={`Rank: ${userRank}`}
            imageClasses="w-10"
            statClasses="font-600"
          />
          <QuizStat
            image="points"
            stat={`Points: ${points}`}
            imageClasses="w-10"
            statClasses="font-600"
          />
        </div>
        <div className="relative">
          <Line
            percent={accuracyPercentage}
            strokeWidth={7}
            trailWidth={7}
            strokeColor="#66B86B"
            trailColor="#66B86B8F"
            strokeLinecap="round"
            className="w-full rounded-full lg:max-w-md"
          />
          <span className="absolute top-0 px-4 text-sm text-white font-600 lg:mt-1">
            Accuracy {accuracyPercentage.toFixed(1)}%
          </span>
        </div>
        {!isPaidUser && (
          <>
            <p className="text-center font-600">
              Want to learn coding and ace these quizzes?
            </p>
            <button
              onClick={() =>
                handleRedirectionWithSignupSource(
                  router.asPath,
                  '/trial-class/booking'
                )
              }
              className="btn btn-orange"
            >
              Try a free lesson
            </button>
          </>
        )}
        {loggedIn ? (
          <button
            onClick={handlePlayAgain}
            className={classNames('btn flex justify-center', {
              'btn-orange': isPaidUser,
              'hover:bg-orange-100 active:bg-orange-200 text-orange': !isPaidUser,
            })}
          >
            {isLoading ? <Spinner classes="w-5" /> : 'Play Again'}
          </button>
        ) : (
          <button
            onClick={onCreateAccountToSave}
            className="btn hover:bg-orange-100 active:bg-orange-200 text-orange"
          >
            Save quiz result
          </button>
        )}
        {!isLessonQuiz ? (
          <Link href={ROUTES.QUIZZES_LIST}>
            <a
              className={classNames('btn text-center', {
                'text-white  bg-green-200 hover:bg-green-100': isPaidUser,
                'hover:bg-orange-100 active:bg-orange-200 text-orange': !isPaidUser,
              })}
            >
              Find a new quiz
            </a>
          </Link>
        ) : (
          <Link href={ROUTES.STUDENT_DASHBOARD_QUIZZES_LIST}>
            <a className="text-center text-white bg-green-200 btn hover:bg-green-100">
              Go back to dashboard
            </a>
          </Link>
        )}
        <div className="flex flex-col space-y-1 font-600">
          <p className="text-center">Share this in social media</p>
          <div className="flex justify-center -ml-1 space-x-1 lg:m-0">
            <FacebookShareButton url={quizUrl} quote={shareText}>
              <FacebookIcon
                borderRadius={40}
                className="w-10 h-10 p-1 duration-100 border border-white rounded-full hover:border-orange focus:outline-none"
              />
            </FacebookShareButton>
            <WhatsappShareButton url={quizUrl} title={shareText}>
              <WhatsappIcon
                borderRadius={40}
                className="w-10 h-10 p-1 duration-100 border border-white rounded-full hover:border-orange focus:outline-none"
              />
            </WhatsappShareButton>
            <TelegramShareButton url={quizUrl} title={shareText}>
              <TelegramIcon
                borderRadius={40}
                className="w-10 h-10 p-1 duration-100 border border-white rounded-full hover:border-orange focus:outline-none"
              />
            </TelegramShareButton>
          </div>
        </div>
        <div className="h-0 border bg-grey-400 border-grey-400" />
        <div className="flex flex-col space-y-3">
          <p className="text-lg font-600">Quiz result</p>
          <div className="flex space-x-10">
            <div className="flex flex-col">
              <p className="text-lg font-600">
                {totalProblemsAnsweredCorrectly}
              </p>
              <p>Correct</p>
            </div>
            <div className="flex flex-col">
              <p className="text-lg font-600">
                {problems.length - totalProblemsAnsweredCorrectly}
              </p>
              <p>Incorrect</p>
            </div>
          </div>
        </div>
        <div className="h-0 border bg-grey-400 border-grey-400" />
        <div className="flex flex-col space-y-3">
          <p className="text-lg font-600">Question Performance</p>
          <div className="flex flex-col space-y-5">
            {problems.map((problem, index) => (
              <QuizSummaryProblemCard
                problem={problem}
                key={problem.id}
                problemNumber={index + 1}
                areCorrectOptionsShown={isLessonQuiz}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Result
